import React, {FunctionComponent, useState} from "react";
import {Alert, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {PlanSelection} from "./plan-selection";
import {useSignupProducts} from "../../hooks/use-signup-products";
import {useBreederInfo} from "../../hooks/use-breeders";
import {PaymentService} from "../../services/payment-service";

type ResumeCheckoutProps = {
    /** The breeder is in a local trial without Creem subscription */
    trial?: boolean;
};

export const ResumeCheckout: FunctionComponent<ResumeCheckoutProps> = ({trial = false}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {data: breederInfo} = useBreederInfo();
    const {products, isPending, isError} = useSignupProducts();
    const [selectedPlan, setSelectedPlan] = useState<string>('yearly');

    const selectedProduct = products.find(product => product.productType === selectedPlan) ?? products[0];

    const {mutate: startCheckout, isPending: isStarting, isError: isStartError} = useMutation({
        mutationKey: ['resume-checkout'],
        mutationFn: async () => {
            const initiatedCheckout = await PaymentService.initiateCheckout(breederInfo.id, selectedProduct.productId);
            window.location.replace(initiatedCheckout.checkoutUrl);
        },
        onError: () => Promise.all([
            queryClient.invalidateQueries({queryKey: ['subscription']}),
            queryClient.invalidateQueries({queryKey: ['payment', 'status']}),
        ]),
    });

    return (
        <Stack spacing={2} alignItems="flex-start" sx={{width: '100%'}}>
            <Typography variant="body2" color="textSecondary">
                {t(trial ? 'subscription.resumeCheckout.descriptionTrial' : 'subscription.resumeCheckout.description')}
            </Typography>
            {isPending && <CircularProgress size={24}/>}
            {!isPending && isError && <Alert severity="error">{t('signupBreeder.plan.loadError')}</Alert>}
            {products.length > 0 && (
                <PlanSelection products={products} selected={selectedProduct?.productType}
                               onSelect={setSelectedPlan}/>
            )}
            {isStartError && <Alert severity="error">{t('subscription.resumeCheckout.error')}</Alert>}
            <Button variant="contained" disabled={isStarting || !selectedProduct} onClick={() => startCheckout()}>
                {isStarting ? t('signupBreeder.submitting') : t('subscription.resumeCheckout.button')}
            </Button>
        </Stack>
    );
};
